import { addListener } from '@reduxjs/toolkit';
import type { Middleware } from 'redux';

import { getLogger } from '@disy/lib/logging';

import { Command } from 'cadenza/api-client/workbook-commands';
import type {
  ExecuteCommandPayload,
  SyncActionPayload,
  SyncSelectionPayload,
} from 'cadenza/multi-windows/synchronization/message-definitions';
import type { WindowsSyncChannelType } from 'cadenza/multi-windows/synchronization/windows-sync-channel';
import {
  getCurrentWindowsSyncChannel,
  getWindowsSyncChannel,
} from 'cadenza/multi-windows/synchronization/windows-sync-channel';
import { getPrimaryStore, getStore } from 'cadenza/store/cadenza-store';
import type { SelectionRecordsDto } from 'cadenza/workbook/selection/model/selection-records-dto';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { forwardExecuteCommand } from 'cadenza/workbook/workbook.state';

const logger = getLogger('cadenza/windows-sync');

function isActionWithType(action: unknown): action is { type: string } {
  return (typeof action === 'object' || typeof action === 'function') && action !== null && 'type' in action;
}

/**
 * Creates a middleware that forwards actions of the specified types to the windows sync channel.
 * Optionally, also block them from being executed locally
 */
export function createActionsSyncReduxMiddleware(
  windowsSyncChannel: WindowsSyncChannelType,
  actionTypesToSync: string[],
): Middleware {
  return () => (next) => (action) => {
    if (isActionWithType(action) && actionTypesToSync.includes(action.type)) {
      logger.debug('Sending action to windows sync channel:', action);
      windowsSyncChannel.sendMessage('SYNC_ACTION', { action });
      return next(action);
    }
    return next(action);
  };
}

/**
 * Sets up the secondary window for syncing actions and executing commands.
 * @param channelName
 */
export function setupSecondaryWindowSync(channelName: string) {
  const channel = getWindowsSyncChannel(channelName);
  syncActionsFromChannel(channel);
  getStore().dispatch(
    addListener({
      actionCreator: forwardExecuteCommand,
      effect: (action) => {
        forwardExecuteCommandToPrimaryWindow(action.payload);
      },
    }),
  );
}

export function requestViewMetadataSync() {
  getCurrentWindowsSyncChannel().sendMessage('REQUEST_VIEW_METADATA_SYNC', null);
}

/**
 * Subscribes to SYNC_ACTION events on the windows sync channel and execute the actions on the local (primary) store.
 */
export function syncActionsFromChannel(windowsSyncChannel: WindowsSyncChannelType) {
  windowsSyncChannel.subscribe('SYNC_ACTION', ({ action }: SyncActionPayload) => {
    logger.debug('Received action from windows sync channel, dispatching it to the primary Redux store:', action);
    // actions should always be synced to the primary store
    getPrimaryStore().dispatch(action);
  });
}

/**
 * Subscribes to EXECUTE_COMMAND events on the windows sync channel and dispatch the execute command thunk on the local (primary) store.
 */
export function executeCommandsFromChannel(windowsSyncChannel: WindowsSyncChannelType) {
  windowsSyncChannel.subscribe('EXECUTE_COMMAND', ({ command }: ExecuteCommandPayload) => {
    logger.debug(
      'Received execute commands message from windows sync channel, dispatching the execute command thunk',
      command,
    );
    getPrimaryStore().dispatch(executeCommand(command));
  });
}

/**
 * Sends the commands to be executed to the primary window via the windows sync channel.
 */
function forwardExecuteCommandToPrimaryWindow(command: Command) {
  logger.debug('Forwarding execute command to primary window:', command);
  getCurrentWindowsSyncChannel().sendMessage('EXECUTE_COMMAND', { command });
}

/**
 * Subscribes to SYNC_SELECTION events and syncs selection to the secondary store in the primary window, if present.
 */
export function syncSelectionToSecondaryStoreFromChannel(windowsSyncChannel: WindowsSyncChannelType) {
  windowsSyncChannel.subscribe('SYNC_SELECTION', ({ selection }: SyncSelectionPayload) => {
    if (getStore() === getPrimaryStore()) {
      return;
    }
    getStore().dispatch(executeCommand(Command.SET_SELECTION({ selection })));
  });
}

/**
 * Sends the selection command to the primary window via the windows sync channel.
 */
export function forwardSelectionForSecondaryStoreToPrimaryWindow(selection?: SelectionRecordsDto) {
  logger.debug('Forwarding selection to primary window for active dialog store sync');
  getCurrentWindowsSyncChannel().sendMessage('SYNC_SELECTION', { selection });
}
